import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import flow from 'lodash/fp/flow';
import { convertFromRaw } from 'draft-js';
import axios from 'axios';

import { List, Button } from 'antd'; // 🚨 Removidos Dropdown e Menu daqui
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons';

import { useAuth } from 'hooks';
import { mentionReplace, lineBrakeToBr, isJSON, serializeToHtml } from 'lib/helper';
import { PROFILE_TYPES } from 'lib/constants';
import { getSubComments, resetSubComments } from 'store/actions/postsActions';
import { makeSubComments, makeGetPostSelector } from 'store/selectors/postSelectors';
import ProfileSwitch from 'components/Groups/NewPost/ProfileSwitch';

import SubCommentTextEditor from '../../Common/SubCommentTextEditor/SubCommentTextEditor';
import SubComment from '../SubComment/SubComment';

import {
  AvatarWrapper,
  CommentsContainer,
  EditorWrapper,
  commentsWrapper,
} from './SubCommentsStyles';

const getCSubComments = makeSubComments();
const getPost = makeGetPostSelector();

const SubComments = props => {
  const {
    itemId,
    postId,
    showNewComment,
    showSubComments,
    totalSubComments = 0,
    groupId,
    groupData,
  } = props;
  const { t } = useTranslation();

  const [isCommentLoading, setIsCommentLoading] = useState(false);

  const pageSize = 10;

  // const { userId, userInfo, userPermissions } = useAuth();
  const { userInfo } = useAuth();
  const { userId, name: userName } = userInfo.user;

  // #region REDUX
  const dispatch = useDispatch();
  const subComments = useSelector(state => getCSubComments(state, itemId));

  const postInfo = useSelector(state => (postId ? getPost(state, postId) : null));

  const finalGroupData = useMemo(() => {
    return (
      groupData ||
      postInfo?.page ||
      (postInfo?.author?.type === PROFILE_TYPES.group
        ? { ...postInfo.author, pageId: postInfo.author.profileId }
        : null)
    );
  }, [groupData, postInfo?.page, postInfo?.author]);

  const pageId = finalGroupData?.pageId || finalGroupData?.id || finalGroupData?.profileId;

  const storeKey = groupId ? 'NEW_POST_GROUP' : `COMMENT_${postId}`;
  const selectedProfile = useSelector(state => state.newPost.posts[storeKey]?.profile);
  // #endregion

  const [isFeedManager, setIsFeedManager] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const checkAdminStatus = async () => {
      // Se não for relacionado a um grupo/página, não é manager
      if (!pageId) {
        if (isMounted) setIsFeedManager(false);
        return;
      }

      try {
        const { data } = await axios.get(`/api/profile/getPageAdmOrManager?pageId=${pageId}`);
        if (isMounted) setIsFeedManager(!!data);
      } catch (error) {
        console.error('Erro ao verificar permissões de admin/gestor nos subcomentários:', error);
        if (isMounted) setIsFeedManager(false);
      }
    };

    checkAdminStatus();

    return () => {
      isMounted = false;
    };
  }, [pageId]);

  const defaultUser = {
    profileId: userId,
    name: userName,
    profileType: PROFILE_TYPES.user,
  };

  let activeProfile = defaultUser;

  if (isFeedManager && finalGroupData) {
    if (selectedProfile && selectedProfile.profileType === PROFILE_TYPES.group) {
      activeProfile = {
        profileId: finalGroupData.pageId || finalGroupData.id || finalGroupData.profileId,
        name: finalGroupData.name || finalGroupData.title,
        profileType: PROFILE_TYPES.group,
        mediaId: finalGroupData.mediaId,
        profilePhoto: finalGroupData.profilePhoto || finalGroupData.avatar,
      };
    } else if (!selectedProfile) {
      activeProfile = {
        profileId: finalGroupData.pageId || finalGroupData.id || finalGroupData.profileId,
        name: finalGroupData.name || finalGroupData.title,
        profileType: PROFILE_TYPES.group,
        mediaId: finalGroupData.mediaId,
        profilePhoto: finalGroupData.profilePhoto || finalGroupData.avatar,
      };
    }
  }

  const fetchSubComments = useCallback(
    async page => {
      setIsCommentLoading(true);
      try {
        await dispatch(getSubComments(itemId, page, pageSize));
      } finally {
        setIsCommentLoading(false);
      }
    },
    [dispatch, pageSize, itemId]
  );

  useEffect(() => {
    if (showSubComments) {
      dispatch(resetSubComments(itemId));
      fetchSubComments(1);
    }
  }, [dispatch, fetchSubComments, itemId, showSubComments]);

  const renderNewCommentEditor = () => {
    return (
      <div style={{ display: 'flex' }}>
        <AvatarWrapper>
          <ProfileSwitch storeKey={storeKey} groupData={groupData} origin="comment" />
          {/* <ProfileSwitch storeKey="NEW_POST_GROUP" groupData={finalGroupData} /> */}
        </AvatarWrapper>
        <EditorWrapper>
          <SubCommentTextEditor
            placeholder={t('comments.leaveComment')}
            postId={itemId}
            groupId={groupId}
            isSubComment={true}
            commentId={itemId}
            activeProfile={activeProfile}
          />
        </EditorWrapper>
      </div>
    );
  };

  return (
    <CommentsContainer>
      {showNewComment && renderNewCommentEditor()}
      {showSubComments && subComments && subComments.length > 0 && (
        <RenderSubComments
          subComments={subComments}
          commentsCount={totalSubComments}
          isCommentLoading={isCommentLoading}
          userId={userId}
          onGetComments={() => fetchSubComments(Math.floor(subComments.length / pageSize) + 1)}
          groupId={groupId}
          groupData={finalGroupData}
        />
      )}
    </CommentsContainer>
  );
};

const RenderSubComments = ({
  subComments,
  commentsCount,
  isCommentLoading,
  userId,
  onGetComments,
  groupId,
  groupData,
}) => {
  const { t } = useTranslation();

  const shouldNotFetchComments = commentsCount === 0 || subComments.length >= commentsCount;

  const isLoading = isCommentLoading && subComments.length === 0;
  const loadMore =
    !shouldNotFetchComments && subComments.length > 0 ? (
      <Button style={{ marginTop: 12 }} loading={isCommentLoading} onClick={onGetComments} block>
        {t('comments.moreComments')}
      </Button>
    ) : null;

  const loading = isLoading
    ? {
        indicator: <FontAwesomeIcon icon={faSpinnerThird} size="3x" spin />,
      }
    : false;

  const getCommentText = comment => {
    if (isJSON(comment.subComment.description)) {
      const contentState = convertFromRaw(JSON.parse(comment.subComment.description));
      return serializeToHtml(contentState);
    }
    return flow(mentionReplace, lineBrakeToBr)(comment.mentions, comment.subComment.description);
  };

  const getSubCommentCount = comment => {
    return comment.subCommentCount || 0;
  };

  return (
    <List
      css={commentsWrapper}
      loadMore={loadMore}
      dataSource={subComments}
      split={false}
      rowKey={comment => comment.subComment.commentId}
      loading={loading}
      renderItem={comment => (
        <List.Item>
          <SubComment
            commentText={getCommentText(comment)}
            fullComment={comment}
            userId={userId}
            subCommentCount={getSubCommentCount(comment)}
            groupId={groupId}
            groupData={groupData}
          />
        </List.Item>
      )}
    />
  );
};

export default SubComments;
